import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { LenderApproval, CompareApprovalsTab, ApprovalBadge, GqlCreditSubmissionApproval } from '../types';
import { COMPARISON_ROWS } from '../constants';
import { computeBadges, computeConditionsBadges, mapCreditSubmissionToApproval } from '../lib';
import { GET_APPROVALS_BY_VENDOR_OPPORTUNITY_ID } from '../queries';
import { EXAMPLE_APPROVAL } from '../lib/mockData';

export const useCompareApprovals = (dynamicsVoId?: string) => {
  const [activeTab, setActiveTab] = useState<CompareApprovalsTab>('approvals');

  const { data, loading, error } = useQuery(GET_APPROVALS_BY_VENDOR_OPPORTUNITY_ID, {
    variables: { voId: dynamicsVoId },
    skip: !dynamicsVoId,
  });

  const approvals = useMemo<LenderApproval[]>(() => {
    const raw: GqlCreditSubmissionApproval[] = data?.creditSubmissionsByDynamicsVOId ?? [];

    const mapped = raw
      .filter((cs): cs is GqlCreditSubmissionApproval => cs != null && cs.creditDecisionParsedData?.parsedData != null)
      .map(mapCreditSubmissionToApproval);

    // Show the example card whenever there are 0 or 1 real approvals.
    return mapped.length <= 1 ? [...mapped, EXAMPLE_APPROVAL] : mapped;
  }, [data]);

  const badgesByRow = useMemo(() => {
    // Badges are only meaningful for real approvals – exclude the example card.
    const realApprovals = approvals.filter(a => !a.isExample);
    const badges: Record<string, Record<string, ApprovalBadge>> = {};

    COMPARISON_ROWS.forEach(row => {
      if (row.key === 'conditions') {
        badges[row.key] = computeConditionsBadges(realApprovals);
      } else {
        badges[row.key] = computeBadges(realApprovals, row);
      }
    });

    return badges;
  }, [approvals]);

  return {
    activeTab,
    setActiveTab,
    approvals,
    badgesByRow,
    loading,
    error,
  };
};
